/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import {
  Controls,
  Background,
  Connection,
  NodeChange,
  EdgeChange,
  ReactFlow,
  Edge,
  ControlButton,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { gql, useApolloClient } from "@apollo/client";
import { useCallback, useContext, useMemo, useEffect, useState } from "react";
import { SUPPORTED_FILE_TYPES } from "../Shared/SupportedFileTypes";
import {
  FaExchangeAlt,
  FaSignInAlt,
  FaSignOutAlt,
  FaLayerGroup,
} from "react-icons/fa";
import { GoDash, GoPlus, GoScreenFull, GoScreenNormal } from "react-icons/go";
import { AxiosContext } from "../Utils/AuthContext";
import JSZip from "jszip";

// Dexter general components
import {
  exportCanvasToPng,
  isNodeTitleValid,
  nodeHasEdge,
  ReactFlowEdge,
  ReactFlowNode,
} from "./TransformNodes/Util/TransformEngineDataUtils";
import GetFile from "../Api/Gql/GetFile";
import { ProjectContext } from "../UserApp";
import { ProjectStorableState } from "../ProjectState/ProjectModels";
import { stringify } from "../ProjectState/ProjectPersistence";
import { DeleteConfirmationModal } from "../Shared/DeleteConfirmationModal";
import { TransformEngineInputNodeComponent } from "./TransformNodes/TransformEngineInputNodeComponent";
import { Endpoints, fileDownload } from "../Utils/Common";
import {
  NO_CANVAS_BACKGROUND,
  useDexterTheme,
} from "../DesignSystem/Theme/ThemeContext";
import { loggedNotify, Component } from "../Shared/Notify";

// Transform engine components
import { ControlBarButton } from "./Components/ControlBarButton";

import { ProjectTitleEditor } from "./Components/ProjectTitleEditor";
import TransformSidePanel, {
  DEFAULT_TRANSFORM_PANEL_WIDTH,
} from "./Components/TransformSideBar";
import { ProtoToXmlTransformComponent_Full } from "./TransformNodes/PX/ProtoToXmlTransformComponent_Full";
import { OutputNodeComponent } from "./OutputNodes/OutputNode";
import { ComposerNodeComponent } from "./OutputNodes/ComposerNode";
import {
  ComposerNodeDescriptor,
  FileInfo,
  FileType,
  InputNodeDescriptor,
  OutputNodeDescriptor,
} from "Api";
import { LoadMappingModal } from "../Shared/LoadMappingModal";
import { XmlToProtoTransformComponent_Full } from "./TransformNodes/XP/XmlToProtoTransformComponent_Full";
import { XmlToProtoTransformComponent_Prim } from "./TransformNodes/XP/XmlToProtoTransformComponent_Prim";
import {
  TransformEngineRules,
  ConnectionRuleContext,
} from "./TransformNodes/Rules/TransformEngineRules";
import { getHandleFromString } from "./TransformNodes/Util/TransformEngineDataUtils";
import { NODE_TYPES } from "../Shared/TransformNodeTypes";
import { ProtoToXmlTransformComponent_Prim } from "./TransformNodes/PX/ProtoToXmlTransformComponent_Prim";
import { SaveAsNewModal } from "../Shared/SaveAsNewModal";
import { EdgeContextMenu } from "./Components/EdgeMenu";
import { PLACEMENT, StatefulTooltip } from "baseui/tooltip";
import { getTooltipOverrides } from "../DesignSystem/TooltipStyles";
import InputsSidePanel from "./Components/InputsSidePanel";
import { TransformEngineHeader } from "./Components/TransformEngineHeader";
import NewMappingModal from "./Components/NewMappingModal";
import { useDexterFileUpload } from "../Shared/Upload/UseDexterFileUpload";
import { PopulateModal } from "./Components/PopulateTransformEngineModal";

interface TransformEngineProps {
  isFocusing: boolean;
  setIsFocusing: React.Dispatch<React.SetStateAction<boolean>>;
  isActive: boolean;
  // Optional caller-supplied node (e.g. a List/Canvas view toggle) rendered
  // centred in this component's header bar.
  viewToggle?: React.ReactNode;
}

export function TransformEngine({
  isFocusing,
  setIsFocusing,
  isActive,
  viewToggle,
}: TransformEngineProps): JSX.Element {
  const projectContext = useContext(ProjectContext);
  const { projectState, projectDispatch } = projectContext;
  const lastLoadedProjectIDRef = React.useRef<string | null>(null);
  const reconcileAfterReplayRef = React.useRef(false);

  const [contextMenu, setContextMenu] = useState<ContextMenu | null>(null);
  const [height, setHeight] = React.useState(window.innerHeight);
  const [showTransformMenu, setShowTransformMenu] = React.useState(false);
  const [showInputMenu, setShowInputMenu] = React.useState(false);
  const [loadMappingModalOpen, setLoadMappingModalOpen] = React.useState(false);
  const [saveAsNewModalOpen, setSaveAsNewModalOpen] = React.useState(false);
  const [newMappingModalOpen, setNewMappingModalOpen] = React.useState(false);
  const [pendingCanvasAction, setPendingCanvasAction] = React.useState<
    "newMapping" | "clear"
  >("newMapping");
  // const [isPopulatingDemo, setIsPopulatingDemo] = React.useState(false);
  const [_nodesToIgnore, setNodesToIgnore] = React.useState<string[]>([]);
  const nodeRefs = React.useRef<ReactFlowNode[]>([]);

  const { uploadFile, removeFile: removeUploadedFile } = useDexterFileUpload();
  const [uploadModalOpen, setUploadModalIsOpen] = React.useState(false);

  // Show the welcome page of the initialization modal only once per login.
  // Stored in localStorage so it survives in-app tab navigation (which clears
  // sessionStorage) and browser tab close/reopen. Cleared on logout so the
  // next login shows it again.
  // const [hasShownInitModal, setHasShownInitModal] = React.useState<boolean>(
  //   () => {
  //     try {
  //       return localStorage.getItem("dexter.hasShownInitModal") === "true";
  //     } catch {
  //       return false;
  //     }
  //   }
  // );

  React.useEffect(() => {
    nodeRefs.current = projectState.rfNodes;
  }, [projectState.rfNodes]);

  const [ruleEngine] = useState(() => new TransformEngineRules());

  const closeMappingLoadModal = (): void => setLoadMappingModalOpen(false);

  interface ContextMenu {
    x: number;
    y: number;
    edge: Edge;
  }

  const handleEdgeContextMenu = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      event.preventDefault();

      const deselectChanges: EdgeChange[] = projectState.rfEdges.map((e) => ({
        id: e.id,
        type: "select",
        selected: false,
      }));
      projectDispatch([
        "changeEdges",
        { changes: deselectChanges },
        projectDispatch,
      ]);

      const selectChange: EdgeChange = {
        id: edge.id,
        type: "select",
        selected: true,
      };
      projectDispatch([
        "changeEdges",
        { changes: [selectChange] },
        projectDispatch,
      ]);

      setContextMenu({
        x: event.clientX,
        y: event.clientY,
        edge,
      });
    },
    [projectDispatch, projectState.rfEdges]
  );

  const closeEdgeMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  useEffect(() => {
    const handleResize = (): void => {
      setHeight(window.innerHeight);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [height]);

  const { currentTheme, canvasBackground } = useDexterTheme();

  const [reactFlowInstance, setReactFlowInstance] = React.useState(null);

  useEffect(() => {
    projectDispatch(["reThemeEdges", { currentTheme }]);
  }, [currentTheme, projectDispatch]);

  const fitViewToNodes = useCallback(() => {
    if (reactFlowInstance) {
      // Small delay to ensure nodes are rendered
      setTimeout(() => {
        reactFlowInstance.fitView({
          padding: 0.2, // 20% padding around the nodes
          duration: 400, // smooth animation
          maxZoom: 1.5, // prevent zooming in too much
        });
      }, 100);
    }
  }, [reactFlowInstance]);

  const apolloClient = useApolloClient();
  const axiosContext = useContext(AxiosContext);

  {
    /* Connects ReactFlow to State Machine */
  }
  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      projectDispatch(["changeNodes", { changes }, projectDispatch]);
    },
    // projectDispatch is guaranteed to be stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) =>
      projectDispatch(["changeEdges", { changes }, projectDispatch]),
    // projectDispatch is guaranteed to be stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onConnect = useCallback(
    (params: Connection) => {
      const { rfNodes, rfEdges } = projectState;
      const sourceNode = rfNodes.find((n) => n.id === params.source);
      const targetNode = rfNodes.find((n) => n.id === params.target);

      if (!sourceNode || !targetNode) return;

      const context: ConnectionRuleContext = {
        sourceNode,
        targetNode,
        sourceHandle: getHandleFromString(sourceNode.data, params.sourceHandle),
        targetHandle: getHandleFromString(targetNode.data, params.targetHandle),
        existingEdges: rfEdges,
        allNodes: rfNodes,
      };

      // Validate and show notification if invalid
      if (ruleEngine.isConnectionValid(params, context, axiosContext)) {
        projectDispatch(["connect", { params }, projectDispatch]);
      }
      // Notification automatically shown if invalid
    },
    [projectState, ruleEngine, projectDispatch, axiosContext]
  );

  const onSaveMapping = useCallback(
    () => projectDispatch(["saveMapping", {}, projectDispatch]),
    // projectDispatch is guaranteed to be stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onSaveMappingAsNew = useCallback(
    (newName: string) => {
      projectDispatch(["saveMappingAsNew", { newName }, projectDispatch]);
      setSaveAsNewModalOpen(false);
    },
    [projectDispatch]
  );

  const startProjectTitleEditing = useCallback(
    () => {
      projectDispatch(["startProjectTitleEditing"]);
    },
    // projectDispatch is guaranteed to be stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const updateProjectTitle = useCallback(
    (wipTitle: string) => {
      projectDispatch(["updateProjectTitle", { wipTitle }]);
    },
    // projectDispatch is guaranteed to be stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const confirmProjectTitleEdit = useCallback(
    () => {
      projectDispatch(["confirmProjectTitleEdit"]);
    },
    // projectDispatch is guaranteed to be stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const discardProjectTitleEdit = useCallback(
    () => {
      projectDispatch(["discardProjectTitleEdit"]);
    },
    // projectDispatch is guaranteed to be stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleEdgeClick = useCallback(
    () => {
      projectDispatch(["edgeClicked"]);
    },
    // projectDispatch is guaranteed to be stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Maintain list of nodes to delete in bulk
  const [nodesToDelete, setNodesToDelete] = React.useState<string[]>([]);

  const confirmDeleteNodes = useCallback(
    async (nodeIdsOverride?: string[]) => {
      const nodesToRemove = nodeIdsOverride ?? nodesToDelete;
      if (nodesToRemove.length === 0) return;

      // Create node removal changes
      const changes: NodeChange[] = nodesToRemove.map((nodeId) => ({
        id: nodeId,
        type: "remove",
      }));

      // Remove nodes from React Flow
      onNodesChange(changes);

      setIsDeleteConfirmationOpen(false);
      setTimeout(() => setNodesToDelete([]), 150);
    },
    [nodesToDelete, onNodesChange]
  );

  const handleDeleteSelectedNodes = useCallback(() => {
    const selectedNodes = projectState.rfNodes.filter((node) => node.selected);
    if (selectedNodes.length === 0) return;

    const nodeIds = selectedNodes.map((node) => node.id);
    const anyHasEdge = selectedNodes.some((node) =>
      nodeHasEdge(projectState.rfEdges, node.data.dataID)
    );

    if (selectedNodes.length > 1 || anyHasEdge) {
      setNodesToDelete(nodeIds);
      setIsDeleteConfirmationOpen(true);
    } else {
      confirmDeleteNodes(nodeIds);
    }
  }, [projectState.rfNodes, projectState.rfEdges, confirmDeleteNodes]);

  const handleDeleteSelectedEdge = useCallback(
    (selectedEdges: ReactFlowEdge[]) => {
      const changes: EdgeChange[] = selectedEdges.map((edge) => ({
        id: edge.id,
        type: "remove",
      }));
      projectDispatch(["changeEdges", { changes }, projectDispatch]);
    },
    [projectDispatch]
  );

  const latestEdgeDeselectRef = React.useRef({
    rfNodes: projectState.rfNodes,
    rfEdges: projectState.rfEdges,
    projectDispatch,
  });

  latestEdgeDeselectRef.current = {
    rfNodes: projectState.rfNodes,
    rfEdges: projectState.rfEdges,
    projectDispatch,
  };

  React.useEffect(() => {
    const { rfNodes, rfEdges, projectDispatch } = latestEdgeDeselectRef.current;
    const selectedNodes = rfNodes.filter((node) => node.selected);

    const selectedEdges = rfEdges.filter((edge) => edge.selected);

    if (selectedNodes.length > 0) {
      const changes: EdgeChange[] = selectedEdges.map((edge) => ({
        id: edge.id,
        type: "select",
        selected: false,
      }));
      projectDispatch(["changeEdges", { changes }, projectDispatch]);
    }
  }, []);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (!isActive) return;
      // Only handle delete if not in an input field
      if (
        (event.target as HTMLElement).tagName === "INPUT" ||
        (event.target as HTMLElement).tagName === "TEXTAREA"
      ) {
        return;
      }

      if (event.key === "Delete" || event.key === "Backspace") {
        const selectedNodes = projectState.rfNodes.filter(
          (node) => node.selected
        );

        const selectedEdges = projectState.rfEdges.filter(
          (edge) => edge.selected
        );

        if (contextMenu != null) {
          event.preventDefault();
          const changes: EdgeChange[] = [
            { id: contextMenu.edge.id, type: "remove" },
          ];
          projectDispatch(["changeEdges", { changes }, projectDispatch]);
          closeEdgeMenu();
          return;
        }

        if (selectedNodes.length > 0) {
          event.preventDefault();
          handleDeleteSelectedNodes();
        } else if (selectedEdges.length > 0) {
          event.preventDefault();
          handleDeleteSelectedEdge(selectedEdges);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    projectState.rfNodes,
    projectState.rfEdges,
    handleDeleteSelectedNodes,
    handleDeleteSelectedEdge,
    projectDispatch,
    closeEdgeMenu,
    contextMenu,
    isActive,
  ]);

  React.useEffect(() => {
    const handleDeleteEvent = (): void => {
      handleDeleteSelectedNodes();
    };

    window.addEventListener(
      "deleteSelectedNodes",
      handleDeleteEvent as EventListener
    );
    return () =>
      window.removeEventListener(
        "deleteSelectedNodes",
        handleDeleteEvent as EventListener
      );
  }, [handleDeleteSelectedNodes]);

  // Need these for dropping node in center of mouse area
  // Corresponds to parallelogram measurements
  const TRANSFORM_NODE_WIDTH = 120;
  const TRANSFORM_NODE_HEIGHT = 40;

  const createInputNode = React.useCallback(
    async (
      fileId: string,
      fileName: string,
      position: { x: number; y: number }
    ): Promise<void> => {
      try {
        // 1. Fetch FileInfo via GraphQL
        const { data, errors } = await apolloClient.query({
          query: gql(GetFile),
          variables: { id: fileId },
          fetchPolicy: "network-only",
        });

        // Handle GraphQL errors
        if (errors && errors.length > 0) {
          loggedNotify.negative(
            axiosContext,
            Component.TRANSFORM_ENGINE,
            `Failed to fetch file info for ${fileName}`
          );
          return;
        }

        // Handle missing FileInfo
        const fileInfo = data?.getFile;
        if (!fileInfo) {
          loggedNotify.negative(
            axiosContext,
            Component.TRANSFORM_ENGINE,
            `File info missing for ${fileName}`
          );
          return;
        }

        // We should use the "userSelectedFileType" in case the
        // ml classifier made a mistake.
        const fileType = fileInfo.userSelectedFileType;
        if (!fileType || fileType === "UNDETECTED") {
          loggedNotify.negative(
            axiosContext,
            Component.TRANSFORM_ENGINE,
            `Cannot create node: Missing or unknown file type for ${fileName}`
          );
          return;
        }

        // 2. Ensure the input-node descriptor exists for this file.
        await axiosContext.post(Endpoints.CreateInputNodeDescriptor, {
          fileType,
          fileId,
          fileName,
        });

        // 3. Parse the file to populate the descriptor's paths. This is the same
        //    endpoint the upload flow used; unlike the create endpoint's silent
        //    fallback, it reports `hasPaths`, so a file that parses to nothing
        //    surfaces as an error instead of a blank node.
        const parseResponse = await axiosContext.post(Endpoints.ParseFiles, {
          fileId: [fileId],
        });
        const parseResult = parseResponse.data?.results?.[0];
        if (!parseResult?.hasPaths) {
          loggedNotify.negative(
            axiosContext,
            Component.TRANSFORM_ENGINE,
            `Couldn't parse ${fileName} to create an input node ${
              parseResult?.error ? `: ${parseResult.error}` : ""
            }`
          );
          return;
        }

        // 4. Fetch the now-populated descriptor and create the node.
        const descriptorResponse = await axiosContext.get(
          Endpoints.GetInputNodeDescriptor,
          { params: { fileId } }
        );
        const descriptor: InputNodeDescriptor = descriptorResponse.data;

        position.x -= 50;
        position.y -= 30;

        projectDispatch(["createNodeFromDescriptor", { descriptor, position }]);
      } catch (err: any) {
        loggedNotify.negative(
          axiosContext,
          Component.TRANSFORM_ENGINE,
          `Unexpected error while creating node for ${fileName}: ${err.message}`
        );
      }
    },
    [apolloClient, axiosContext, projectDispatch]
  );

  const handlePopulateNext = React.useCallback(
    async (uploaded: FileInfo[]) => {
      setUploadModalIsOpen(false);
      if (uploaded.length === 0) return;

      const base = { x: 100, y: 100 };
      // One at a time, for the same reason as the folder drop: parallel
      // creates can clobber each other on the backend.
      for (let index = 0; index < uploaded.length; index++) {
        const file = uploaded[index];
        await createInputNode(file.id, file.attributes?.name ?? "", {
          x: base.x,
          y: base.y + index * 125,
        });
      }

      setNodesToIgnore((prev) => [...prev, ...uploaded.map((file) => file.id)]);
      fitViewToNodes();
    },
    [createInputNode, fitViewToNodes]
  );

  const onDrop = React.useCallback(
    async (event: React.DragEvent) => {
      event.preventDefault();

      const transformData = event.dataTransfer.getData(
        "application/transformnode"
      );
      const inputData = event.dataTransfer.getData("application/inputfile");
      const inputFolderData = event.dataTransfer.getData(
        "application/inputfolder"
      );

      // Convert client (mouse) coordinates to flow coordinates
      const basePosition = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      // Transform node dropped
      if (transformData) {
        const descriptor = JSON.parse(transformData);
        basePosition.x -= TRANSFORM_NODE_WIDTH / 2;
        basePosition.y -= TRANSFORM_NODE_HEIGHT / 2;

        // projectDispatch expects "position"
        const position = basePosition;

        projectDispatch(["createNodeFromDescriptor", { descriptor, position }]);
        return;
      }

      // Input file dropped
      if (inputData) {
        const { fileId, fileName } = JSON.parse(inputData);
        await createInputNode(fileId, fileName, basePosition);
        setNodesToIgnore((prevNodes) => [...prevNodes, fileId]);
        return;
      }

      // Folder dropped: create an input node for every supported file found
      // anywhere beneath it. The panel has already flattened the subtree into
      // index-aligned {fileNames, fileIds, fileTypes} arrays.
      if (inputFolderData) {
        const { fileNames, fileIds, fileTypes } = JSON.parse(inputFolderData);

        if (fileNames.length !== fileIds.length) {
          loggedNotify.negative(
            axiosContext,
            Component.TRANSFORM_ENGINE,
            `Could not create nodes; mismatch between file names and ids.`
          );
        }

        // Create the nodes one at a time, not all at once. Firing them in
        // parallel makes the backend handle them simultaneously, where they can
        // clobber each other and some files silently never get created. Going
        // in order also makes the nodes stack in the same predictable order
        // every time.
        let offsetY = 0;
        for (let index = 0; index < fileIds.length; index++) {
          const rawType = fileTypes[index];
          const fileType = (Object.values(FileType) as string[]).includes(
            rawType
          )
            ? (rawType as FileType)
            : FileType.UNDETECTED;

          if (!SUPPORTED_FILE_TYPES.includes(fileType)) continue;

          await createInputNode(fileIds[index], fileNames[index], {
            x: basePosition.x,
            y: basePosition.y + offsetY,
          });
          offsetY += 125; // stagger each node vertically
        }

        setNodesToIgnore((prevNodes) => [...prevNodes, ...fileIds]);
        return;
      }
    },
    [axiosContext, projectDispatch, createInputNode, reactFlowInstance]
  );

  const fetchSavedProjects = React.useCallback(async () => {
    try {
      const response = await axiosContext.get(Endpoints.GetProjects);
      const projects = response.data?.payload ?? response.data ?? [];

      projectDispatch(["setBufferedProjects", { projects }]);
    } catch {
      projectDispatch(["setBufferedProjects", { projects: [] }]);
    }
  }, [axiosContext, projectDispatch]);

  useEffect(() => {
    if (isActive) void fetchSavedProjects();
  }, [isActive, fetchSavedProjects]);

  {
    /* Clear the Project */
  }
  const onClear = useCallback(
    () => {
      projectDispatch([
        "loadProjectFromData",
        { project: new ProjectStorableState() },
      ]);
    },
    // projectDispatch is guaranteed to be stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const startNewMapping = useCallback(() => {
    onClear();
    setNewMappingModalOpen(true);
  }, [onClear]);

  const clearCanvas = useCallback(() => {
    const edgeChanges: EdgeChange[] = projectState.rfEdges.map((edge) => ({
      id: edge.id,
      type: "remove",
    }));
    if (edgeChanges.length > 0) onEdgesChange(edgeChanges);

    const nodeChanges: NodeChange[] = projectState.rfNodes.map((node) => ({
      id: node.id,
      type: "remove",
    }));
    if (nodeChanges.length > 0) onNodesChange(nodeChanges);
  }, [
    projectState.rfNodes,
    projectState.rfEdges,
    onNodesChange,
    onEdgesChange,
  ]);

  const uploadInputRef = React.useRef<HTMLInputElement>(null);

  const uploadMappingFile = (file: File): void => {
    const reader = new FileReader();
    reader.onload = async (loadEvent) => {
      const fileJsonText = loadEvent.target?.result as string;

      try {
        const parsed = JSON.parse(fileJsonText);

        // 1. Load into project state
        projectDispatch([
          "loadProjectFromJson",
          { json: fileJsonText, source: file.name },
        ]);

        // 2. Wait for state to update (got this through experimentation)
        await new Promise((res) => setTimeout(res, 10));

        // 3. Restore input node descriptors for correct propagation
        if (parsed.inputNodeDescriptors) {
          await axiosContext.post(
            Endpoints.RestoreInputNodeDescriptors,
            parsed.inputNodeDescriptors,
            {
              headers: { "Content-Type": "application/json" },
            }
          );
        }

        // 4. Trigger reconcile manually with silent: true to suppress per-node notifications
        projectDispatch([
          "reconcileConnections",
          { isActiveRef: reconcileAfterReplayRef, silent: true },
          projectDispatch,
        ]);

        loggedNotify.positive(
          axiosContext,
          Component.TRANSFORM_ENGINE,
          "Mapping uploaded and reconciled."
        );

        // FIT VIEW AFTER UPLOAD
        fitViewToNodes();
      } catch (e: any) {
        loggedNotify.negative(
          axiosContext,
          Component.TRANSFORM_ENGINE,
          `Failed to import mapping${e?.message ? `: ${e.message}` : ""}`
        );
      }
    };
    reader.readAsText(file);
  };

  const nameNewMapping = useCallback(
    (name: string) => {
      // Same reducer path as editing the title in the header.
      projectDispatch(["startProjectTitleEditing"]);
      projectDispatch(["updateProjectTitle", { wipTitle: name }]);
      projectDispatch(["confirmProjectTitleEdit"]);
      setNewMappingModalOpen(false);
    },
    // projectDispatch is guaranteed to be stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /** Commented out demo mapping population code for now. Will implement
   * when refactor is complete for the transfomraiton
   */

  // const populateDemoMapping = React.useCallback(async () => {
  //   if (isPopulatingDemo) return;
  //   setIsPopulatingDemo(true);
  //   //Clear project data before loading a new one
  //   onClear();

  //   try {
  //     // 1) Fetch demo JSON from backend
  //     const resp = await axiosContext.get(Endpoints.GetDemoMappingJson, {
  //       responseType: "text",
  //     });
  //     const jsonText: string =
  //       typeof resp.data === "string" ? resp.data : JSON.stringify(resp.data);

  //     // 2) Ensure files exist
  //     await axiosContext.post("/ensureProjectFiles", jsonText, {
  //       headers: { "Content-Type": "application/json" },
  //     });
  //     // 3) Parse JSON retrieved from seed and get projectID
  //     const parsed = JSON.parse(jsonText);
  //     const projectID: string | undefined = parsed?.projectID;
  //     if (!projectID) {
  //       loggedNotify.negative(
  //         axiosContext,
  //         Component.TRANSFORM_ENGINE,
  //         "Demo mapping JSON has no projectID."
  //       );
  //       return;
  //     }

  //     // 4) Load mapping directly into state
  //     projectDispatch([
  //       "loadProjectFromJson",
  //       { json: jsonText, source: "dexter-demo-project.json" },
  //     ]);

  //     // Wait for state to update (got this through experimentation)
  //     await new Promise((res) => setTimeout(res, 10));

  //     // 6) POST descriptors to backend (in case they rely on input file content)
  //     await axiosContext.post(
  //       Endpoints.RestoreInputNodeDescriptors,
  //       parsed.inputNodeDescriptors || []
  //     );

  //     // 7) Trigger full reconcile to get outputs again if necessary
  //     // Pass silent: true to suppress per-node notifications
  //     projectDispatch([
  //       "reconcileConnections",
  //       { isActiveRef: reconcileAfterReplayRef, silent: true },
  //       projectDispatch,
  //     ]);

  //     loggedNotify.positive(
  //       axiosContext,
  //       Component.TRANSFORM_ENGINE,
  //       "Demo mapping loaded successfully."
  //     );

  //     // Ensure all nodes are not in a selected state. Previous issue without this check causing an output node to appear
  //     // as selected by default after loading demo project. This ensures that all nodes base selected state is false
  //     const deselectAllChanges: NodeChange[] = nodeRefs.current.map((node) => ({
  //       id: node.id,
  //       type: "select",
  //       selected: false,
  //     }));

  //     if (deselectAllChanges.length > 0) {
  //       onNodesChange(deselectAllChanges);
  //     }

  //     // FIT VIEW AFTER DEMO LOAD
  //     fitViewToNodes();
  //   } catch (e: any) {
  //     loggedNotify.negative(
  //       axiosContext,
  //       Component.TRANSFORM_ENGINE,
  //       `Failed to load demo mapping${e?.message ? `: ${e.message}` : ""}`
  //     );
  //   } finally {
  //     setIsPopulatingDemo(false);
  //   }
  // }, [
  //   axiosContext,
  //   projectDispatch,
  //   isPopulatingDemo,
  //   onClear,
  //   reconcileAfterReplayRef,
  //   fitViewToNodes,
  //   onNodesChange,
  // ]);

  const addComposerNode = React.useCallback(async () => {
    try {
      const response = await axiosContext.get(
        Endpoints.GetComposerNodeDescriptor
      );

      if (response.status === 200) {
        const descriptor: ComposerNodeDescriptor = response.data;

        const composerNodes = nodeRefs.current.filter(
          (node) => node.type == NODE_TYPES.composer
        );

        const outputNodes = nodeRefs.current.filter(
          (node) => node.type == NODE_TYPES.output
        );

        const transformNodes = nodeRefs.current.filter(
          (node) =>
            node.type === NODE_TYPES.protoToXml_full ||
            node.type === NODE_TYPES.protoToXml_prim ||
            node.type === NODE_TYPES.xmlToProto_full ||
            node.type === NODE_TYPES.xmlToProto_prim
        );

        let x: number;
        let y: number;

        // Check if there are transform nodes without a composer OR output node to their right
        const transformsWithoutOutput = transformNodes.filter(
          (transformNode) => {
            // Check if there's already a composer OR output node positioned near this transform
            const hasComposerNearby = composerNodes.some((composerNode) => {
              const isNearVertically =
                Math.abs(composerNode.position.y - transformNode.position.y) <
                50;
              const isToTheRight =
                composerNode.position.x > transformNode.position.x;
              return isNearVertically && isToTheRight;
            });

            const hasOutputNearby = outputNodes.some((outputNode) => {
              const isNearVertically =
                Math.abs(outputNode.position.y - transformNode.position.y) < 50;
              const isToTheRight =
                outputNode.position.x > transformNode.position.x;
              return isNearVertically && isToTheRight;
            });

            return !hasComposerNearby && !hasOutputNearby;
          }
        );

        const allTransformsHaveOutputs =
          transformNodes.length > 0 && transformsWithoutOutput.length === 0;

        // If there are transforms without composer nodes, place next to the first one
        if (transformsWithoutOutput.length > 0) {
          const targetTransform = transformsWithoutOutput[0];

          const composerNodeNodeWidth = 150; // Approximate output node width
          const horizontalSpacing = 150; // Space between transform and output

          x =
            targetTransform.position.x +
            composerNodeNodeWidth +
            horizontalSpacing;
          y = targetTransform.position.y;
        }
        // Otherwise, stack composer nodes using the existing logic
        else {
          const lastComposerNode =
            composerNodes.length > 0
              ? composerNodes[composerNodes.length - 1]
              : null;

          // Check if this is the first "extra" composer after all transforms are paired
          const composersAtY620 = composerNodes.filter(
            (node) => node.position.y === 620
          );
          const isFirstExtraComposer =
            allTransformsHaveOutputs && composersAtY620.length === 0;

          x = isFirstExtraComposer
            ? 20
            : lastComposerNode
            ? lastComposerNode.position.x + 385
            : 20;
          y = 620;
        }

        projectDispatch([
          "createNodeFromDescriptor",
          { descriptor, position: { x, y } },
        ]);
      } else {
        loggedNotify.negative(
          axiosContext,
          Component.TRANSFORM_ENGINE,
          `HTTP status ${response.status} - Unable to get composer node descriptor`
        );
      }
    } catch (e) {
      loggedNotify.negative(
        axiosContext,
        Component.TRANSFORM_ENGINE,
        "Unable to fetch composer node descriptor"
      );
    }
  }, [axiosContext, projectDispatch]);

  const addOutputNode = React.useCallback(async () => {
    try {
      const response = await axiosContext.get(
        Endpoints.GetOutputNodeDescriptor
      );

      if (response.status === 200) {
        const descriptor: OutputNodeDescriptor = response.data;

        const outputNodes = nodeRefs.current.filter(
          (node) => node.type == NODE_TYPES.output
        );

        const composerNodes = nodeRefs.current.filter(
          (node) => node.type == NODE_TYPES.composer
        );

        const transformNodes = nodeRefs.current.filter(
          (node) =>
            node.type === NODE_TYPES.protoToXml_full ||
            node.type === NODE_TYPES.protoToXml_prim ||
            node.type === NODE_TYPES.xmlToProto_full ||
            node.type === NODE_TYPES.xmlToProto_prim
        );

        let x: number;
        let y: number;

        // Check if there are transform nodes without an output OR composer node to their right
        const transformsWithoutOutput = transformNodes.filter(
          (transformNode) => {
            // Check if there's already an output OR composer node positioned near this transform
            const hasOutputNearby = outputNodes.some((outputNode) => {
              const isNearVertically =
                Math.abs(outputNode.position.y - transformNode.position.y) < 50;
              const isToTheRight =
                outputNode.position.x > transformNode.position.x;
              return isNearVertically && isToTheRight;
            });

            const hasComposerNearby = composerNodes.some((composerNode) => {
              const isNearVertically =
                Math.abs(composerNode.position.y - transformNode.position.y) <
                50;
              const isToTheRight =
                composerNode.position.x > transformNode.position.x;
              return isNearVertically && isToTheRight;
            });

            return !hasOutputNearby && !hasComposerNearby;
          }
        );

        const allTransformsHaveOutputs =
          transformNodes.length > 0 && transformsWithoutOutput.length === 0;

        // If there are transforms without output nodes, place next to the first one
        if (transformsWithoutOutput.length > 0) {
          const targetTransform = transformsWithoutOutput[0];

          const outputNodeWidth = 120; // Approximate output node width
          const horizontalSpacing = 150; // Space between transform and output

          x = targetTransform.position.x + outputNodeWidth + horizontalSpacing;
          y = targetTransform.position.y;
        }
        // Otherwise, stack output nodes using the existing logic
        else {
          const lastOutputNode =
            outputNodes.length > 0 ? outputNodes[outputNodes.length - 1] : null;

          // Check if this is the first "extra" output after all transforms are paired
          const outputsAtY500 = outputNodes.filter(
            (node) => node.position.y === 500
          );
          const isFirstExtraOutput =
            allTransformsHaveOutputs && outputsAtY500.length === 0;

          x = isFirstExtraOutput
            ? 20
            : lastOutputNode
            ? lastOutputNode.position.x + 120
            : 20;
          y = 500;
        }

        projectDispatch([
          "createNodeFromDescriptor",
          { descriptor, position: { x, y } },
        ]);
      } else {
        loggedNotify.negative(
          axiosContext,
          Component.TRANSFORM_ENGINE,
          `HTTP status ${response.status} - Unable to get output node descriptor`
        );
      }
    } catch (e) {
      loggedNotify.negative(
        axiosContext,
        Component.TRANSFORM_ENGINE,
        "Unable to fetch output node descriptor"
      );
    }
  }, [axiosContext, projectDispatch]);

  // Update #1: The height calculation was originally set to `calc(${height}px - 161.55px)`,
  // When the Dexter logo went from "50" to "35", the header bar height decreased from
  // 86 to 71 pixels.  Therefore, instead of subtracting 161.55px, we are now
  // subtracting 146.55px
  // Update #2: The toolbar went from 50px to 25px, so we needed to subtract another
  // 25 pixels, putting this calculation at 119.55px.
  // Update #3: The toolbar is now TransformEngineHeader (48px). The canvas
  // fills the remaining space with flex, so no manual height maths is needed
  // here, but anything outside this file that assumed 25px must be updated.

  async function handleMappingSelect(
    project: ProjectStorableState
  ): Promise<void> {
    // Clear project data before loading new one
    onClear();

    // Wait for state to update (got this through experimentation)
    await new Promise((res) => setTimeout(res, 20));

    if (!project) {
      setLoadMappingModalOpen(false);
      return;
    }

    projectDispatch(["loadProjectFromData", { project }]);

    lastLoadedProjectIDRef.current = project.projectID;

    await axiosContext.post(
      Endpoints.RestoreInputNodeDescriptors,
      project.inputNodeDescriptors
    );

    // Wait for state to update (got this through experimentation)
    await new Promise((res) => setTimeout(res, 20));

    // Pass silent: true to suppress per-node notifications
    projectDispatch([
      "reconcileConnections",
      { isActiveRef: reconcileAfterReplayRef, silent: true },
      projectDispatch,
    ]);

    setLoadMappingModalOpen(false);

    // Show single success notification
    loggedNotify.positive(
      axiosContext,
      Component.TRANSFORM_ENGINE,
      `Mapping "${project.projectName}" loaded successfully.`
    );

    // FIT VIEW AFTER LOADING
    fitViewToNodes();
  }

  async function handleMappingDelete(projectID: string): Promise<void> {
    try {
      // Call backend to delete the project using ID
      await axiosContext.post(Endpoints.DeleteProject, {
        projectId: projectID,
      });

      // Refresh the saved projects list
      await fetchSavedProjects();

      loggedNotify.positive(
        axiosContext,
        Component.TRANSFORM_ENGINE,
        "Mapping deleted successfully"
      );
    } catch (error) {
      loggedNotify.negative(
        axiosContext,
        Component.TRANSFORM_ENGINE,
        "Failed to delete mapping"
      );
    }
  }

  const REACT_FLOW_STYLE: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    // Fill the remaining height left by the toolbar. The child stretches to fill
    flex: 1,
    minHeight: 0,
    width: "100%",
    backgroundColor: currentTheme.colors.reactFlowBackground,
  };

  {
    /* Modal Visibility */
  }

  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    React.useState(false);

  {
    /* Memorized Nodes */
  }
  const nodeTypes = useMemo(
    () => ({
      transformEngineInputNode: TransformEngineInputNodeComponent,
      protoToXmlTransformNode_full: ProtoToXmlTransformComponent_Full,
      protoToXmlTransformNode_prim: ProtoToXmlTransformComponent_Prim,
      xmlToProtoTransformNode_full: XmlToProtoTransformComponent_Full,
      xmlToProtoTransformNode_prim: XmlToProtoTransformComponent_Prim,
      outputNode: OutputNodeComponent,
      composerNode: ComposerNodeComponent,
    }),
    []
  );

  {
    /* Saving and Loading */
  }
  const onDownload = useCallback(() => {
    const stringifiedData = stringify(projectState);
    const nodeData = new Blob([stringifiedData], { type: "application/json" });
    const filename = projectState.projectName + ".json";
    fileDownload(nodeData, filename);
  }, [projectState]);

  const addOutput = useCallback(() => {
    addOutputNode();
  }, [addOutputNode]);

  const addComposer = useCallback(() => {
    addComposerNode();
  }, [addComposerNode]);

  // Need to discuss if we still want a welcome page for users without the use
  // of the initialziation modal

  // useEffect(() => {
  //   if (!hasShownInitModal && projectState.rfNodes.length === 0) {
  //     setInitModalStartPage("welcome");
  //     setTransformInitializationModalIsOpen(true);
  //     setHasShownInitModal(true);
  //     try {
  //       localStorage.setItem("dexter.hasShownInitModal", "true");
  //     } catch {
  //       // localStorage unavailable; non-critical
  //     }
  //   }
  // }, [hasShownInitModal, projectState.rfNodes.length]);

  // Which page the initialization modal opens on. The one-time auto-open lands
  // on "welcome"; the toolbar's Open Initialization lands on "main".
  // const [_initModalStartPage, setInitModalStartPage] = React.useState<
  //   "welcome" | "main"
  // >("main");

  const [selected, setSelected] = useState<
    "input" | "transform" | "output" | "composer" | null
  >(null);

  // Check if any output or composer nodes exist and at least one is
  // in a transformed state.
  const anyOutputReady = projectState.rfNodes.some((node) => {
    if (node.type === NODE_TYPES.output || node.type === NODE_TYPES.composer) {
      return (
        node.data.descriptor as OutputNodeDescriptor | ComposerNodeDescriptor
      )?.isTransformReady;
    }
    return false;
  });

  const handleDownloadZip = async (): Promise<void> => {
    const outputNodes = projectState.rfNodes.filter(
      (node) =>
        node.type === NODE_TYPES.output &&
        (node.data.descriptor as OutputNodeDescriptor)?.isTransformReady
    );

    const composerNodes = projectState.rfNodes.filter(
      (node) =>
        node.type === NODE_TYPES.composer &&
        (node.data.descriptor as ComposerNodeDescriptor)?.isTransformReady
    );

    if (!outputNodes.length && !composerNodes.length) {
      loggedNotify.negative(
        axiosContext,
        Component.TRANSFORM_ENGINE,
        "No output or composer nodes are ready for download."
      );
      return;
    }

    // Shared extraction logic for both output and composer nodes.
    const extractFileData = (
      node: (typeof projectState.rfNodes)[number],
      idx: number
    ): { fileName: string; fileContent: string | null } => {
      let fileContent: string | null = null;
      let fileType: string | null = null;
      let fileExtension = "txt"; // fallback

      const mappingResult = node.data.mappingResult;
      if (typeof mappingResult === "string") {
        const contentMatch = mappingResult.match(/content=(.*), fileType=/s);
        if (contentMatch) fileContent = contentMatch[1].trim();

        const typeMatch = mappingResult.match(/fileType=([A-Za-z]+)/);
        if (typeMatch) fileType = typeMatch[1];
      } else if (typeof mappingResult === "object" && mappingResult !== null) {
        fileContent = mappingResult.content;
        fileType = mappingResult.fileType;
      }

      if (fileType && FileType[fileType as keyof typeof FileType]) {
        fileExtension =
          FileType[fileType as keyof typeof FileType].toLowerCase();
      }

      // 1) Sanitize
      // 2) Create unique file names; only one file will appear in the
      //    zip in the case that the outputs have the same name.
      const rawName =
        (node.data.descriptor as OutputNodeDescriptor | ComposerNodeDescriptor)
          ?.displayName || `node_${idx + 1}`;
      const safeName = rawName.replace(/[^a-z0-9_\-]/gi, "_"); // replace illegal characters
      const fileName = `${safeName}_${idx + 1}.${fileExtension}`;

      return { fileName, fileContent };
    };

    const zip = new JSZip();

    if (outputNodes.length > 0) {
      const outputsFolder = zip.folder("outputs");
      outputNodes.forEach((node, idx) => {
        const { fileName, fileContent } = extractFileData(node, idx);
        if (fileContent) {
          outputsFolder?.file(fileName, fileContent);
        }
      });
    }

    if (composerNodes.length > 0) {
      composerNodes.forEach((node, idx) => {
        const composersFolder = zip.folder("composers");
        const { fileName, fileContent } = extractFileData(node, idx);
        if (fileContent) {
          composersFolder?.file(fileName, fileContent);
        }
      });
    }

    // Generate and trigger download
    // For now, give the archive the mapping name
    const content = await zip.generateAsync({ type: "blob" });
    const fileName = (projectState.projectName || "mapping_outputs") + ".zip";

    const a = document.createElement("a");
    a.href = URL.createObjectURL(content);
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  const handleMenuToggle = (menu: "input" | "transform"): void => {
    if (selected === menu) {
      // If clicking the same button, close it
      setSelected(null);
      setShowInputMenu(false);
      setShowTransformMenu(false);
    } else {
      // Otherwise, open the clicked one and close the other
      setSelected(menu);
      setShowInputMenu(menu === "input");
      setShowTransformMenu(menu === "transform");
    }
  };

  // Custom control components in order to add consistent tooltips
  const ICON_STYLE = { width: 18, height: 18, maxWidth: 18, maxHeight: 18 };

  const TooltipControlButton: React.FC<{
    tooltip: string;
    testId: string;
    onClick: () => void;
    icon: React.ReactNode;
  }> = ({ tooltip, testId, onClick, icon }) => {
    const { currentTheme } = useDexterTheme();
    return (
      <StatefulTooltip
        placement={PLACEMENT.top}
        content={tooltip}
        overrides={getTooltipOverrides(currentTheme)}
      >
        <div style={{ display: "inline-flex" }}>
          <ControlButton data-testid={testId} onClick={onClick}>
            {icon}
          </ControlButton>
        </div>
      </StatefulTooltip>
    );
  };

  function FlowZoomControls(): JSX.Element {
    const { zoomIn, zoomOut } = useReactFlow();
    return (
      <>
        <TooltipControlButton
          tooltip="Zoom In"
          testId="zoom-in-button"
          onClick={() => zoomIn()}
          icon={<GoPlus style={ICON_STYLE} />}
        />
        <TooltipControlButton
          tooltip="Zoom Out"
          testId="zoom-out-button"
          onClick={() => zoomOut()}
          icon={<GoDash style={ICON_STYLE} />}
        />
      </>
    );
  }

  const canvasContainerRef = React.useRef<HTMLDivElement>(null);

  const onDownloadImage = useCallback(async () => {
    const container = canvasContainerRef.current;
    if (!container) return;
    try {
      const blob = await exportCanvasToPng(
        container,
        projectState.rfNodes,
        currentTheme.colors.reactFlowBackground
      );
      if (!blob) {
        loggedNotify.negative(
          axiosContext,
          Component.TRANSFORM_ENGINE,
          "Nothing on the canvas to export."
        );
        return;
      }
      fileDownload(blob, (projectState.projectName || "mapping") + ".png");
    } catch (e: any) {
      loggedNotify.negative(
        axiosContext,
        Component.TRANSFORM_ENGINE,
        `Failed to export image${e?.message ? `: ${e.message}` : ""}`
      );
    }
  }, [
    projectState.rfNodes,
    projectState.projectName,
    currentTheme.colors.reactFlowBackground,
    axiosContext,
  ]);

  const captureMouseEventForUserMenu = useCallback(
    (event: React.MouseEvent) => {
      const target = event.target as HTMLElement;

      // Skip clicks inside any self-managing floating UI — Base Web menus/
      // popovers/dialogs/tooltips, and our own custom EdgeContextMenu. Those
      // already handle real outside clicks correctly; forwarding a synthetic
      // document-targeted click here would make them treat a click on their
      // OWN content as "outside" and close before the item's onClick fires.
      if (
        target.closest(
          '[role="menu"], [role="listbox"], [role="dialog"], [role="tooltip"], [data-baseweb="popover"], [data-context-menu-boundary]'
        )
      ) {
        return;
      }

      document.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
      document.dispatchEvent(
        new PointerEvent("pointerdown", { bubbles: true })
      );
      document.dispatchEvent(new MouseEvent("click", { bubbles: true }));
    },
    []
  );

  return (
    <>
      <PopulateModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalIsOpen(false)}
        uploadFile={uploadFile}
        removeFile={removeUploadedFile}
        onError={(message) =>
          loggedNotify.negative(axiosContext, Component.FILES, message)
        }
        onNext={(files) => void handlePopulateNext(files)}
      />
      <div>
        <DeleteConfirmationModal
          isOpen={isDeleteConfirmationOpen}
          header={
            nodesToDelete.length == 1
              ? `Delete Node`
              : nodesToDelete.length > 1
              ? "Delete Multiple Nodes"
              : pendingCanvasAction === "clear"
              ? "Clear Canvas"
              : "Start a New Mapping"
          }
          bodyText={
            nodesToDelete.length == 1
              ? `Are you sure you want to delete this node? It may have edges attached.`
              : nodesToDelete.length > 1
              ? `Are you sure you want to delete ${nodesToDelete.length} nodes? They may have edges attached.`
              : pendingCanvasAction === "clear"
              ? "Remove all nodes and edges from the canvas?"
              : "Start a new mapping? Unsaved changes to the current one, including all nodes and edges, will be lost."
          }
          confirmLabel={
            nodesToDelete.length > 0
              ? "Delete"
              : pendingCanvasAction === "clear"
              ? "Clear"
              : "Start New Mapping"
          }
          onDelete={() => {
            if (nodesToDelete.length > 0) {
              confirmDeleteNodes();
            } else if (pendingCanvasAction === "clear") {
              setIsDeleteConfirmationOpen(false);
              clearCanvas();
            } else {
              setIsDeleteConfirmationOpen(false);
              startNewMapping();
            }
          }}
          onCancel={() => {
            setIsDeleteConfirmationOpen(false);
            setTimeout(() => setNodesToDelete([]), 150);
          }}
        />
        <SaveAsNewModal
          isOpen={saveAsNewModalOpen}
          currentName={projectState.projectName}
          onSave={onSaveMappingAsNew}
          onCancel={() => setSaveAsNewModalOpen(false)}
        />
        <NewMappingModal
          isOpen={newMappingModalOpen}
          onCreate={nameNewMapping}
          onCancel={() => setNewMappingModalOpen(false)}
        />
        {/* Hidden picker behind Open Mapping → Upload from JSON file */}
        <input
          ref={uploadInputRef}
          type="file"
          accept=".json,application/json"
          style={{ display: "none" }}
          data-testid="upload-mapping-json-input"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) uploadMappingFile(file);
            // Reset so choosing the same file again still triggers onChange.
            event.target.value = "";
          }}
        />
        <LoadMappingModal
          isOpen={loadMappingModalOpen}
          onClose={closeMappingLoadModal}
          mappings={projectState.bufferedProjects.map((projectValue) => ({
            projectID: projectValue.projectID,
            projectName: projectValue.projectName,
            createdAt: (projectValue as any).createdAt ?? Date.now(),
          }))}
          onSelect={handleMappingSelect}
          onDelete={handleMappingDelete}
          itemsPerPage={10}
        />
        {!isFocusing && (
          <TransformEngineHeader
            title={
              <ProjectTitleEditor
                projectName={projectState.projectName}
                projectTitleInProgress={
                  projectState.projectTitleInProgress ?? ""
                }
                editing={projectState.projectTitleEditInProgress}
                showValidationError={
                  (projectState.projectTitleInProgress ?? "").length > 0 &&
                  !isNodeTitleValid(projectState.projectTitleInProgress ?? "")
                }
                onStartEditing={startProjectTitleEditing}
                onUpdate={updateProjectTitle}
                onConfirm={confirmProjectTitleEdit}
                onDiscard={discardProjectTitleEdit}
              />
            }
            viewToggle={viewToggle}
            onNewMapping={() => {
              // Confirm first only when there's work on the canvas to lose.
              if (projectState.rfNodes.length > 0) {
                setPendingCanvasAction("newMapping");
                setIsDeleteConfirmationOpen(true);
              } else {
                startNewMapping();
              }
            }}
            onOpenMapping={() => setLoadMappingModalOpen(true)}
            onUploadMapping={() => uploadInputRef.current?.click()}
            onUploadFiles={() => setUploadModalIsOpen(true)}
            onDownloadConfig={onDownload}
            onDownloadImage={() => void onDownloadImage()}
            canDownloadImage={projectState.rfNodes.length > 0}
            onDownloadOutputs={() => void handleDownloadZip()}
            canDownloadOutputs={anyOutputReady}
            onSave={onSaveMapping}
            onSaveAs={() => setSaveAsNewModalOpen(true)}
            onSaveAndClose={() => {
              // Save under the current name, then show the mappings list.
              onSaveMapping();
              setLoadMappingModalOpen(true);
              // The save request is async, so refresh the list shortly after
              // to pick up the just-saved mapping.
              setTimeout(() => void fetchSavedProjects(), 500);
            }}
            onClearCanvas={() => {
              setPendingCanvasAction("clear");
              setIsDeleteConfirmationOpen(true);
            }}
            canClearCanvas={
              projectState.rfNodes.length > 0 || projectState.rfEdges.length > 0
            }
          />
        )}
      </div>

      <div style={REACT_FLOW_STYLE}>
        <div style={{ display: "flex", flex: 1, minHeight: 0, width: "100%" }}>
          {/* --- Control Bar --- */}
          <div
            style={{
              width: "50px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              paddingTop: "16px",
              backgroundColor: currentTheme.colors.backgroundTertiary,
              borderRight: `1px solid ${currentTheme.colors.text}`, // Divider for output node
              boxShadow: "2px 0 6px rgba(0,0,0,0.06)",
            }}
          >
            <ControlBarButton
              selected={selected === "input"}
              icon={
                <FaSignInAlt
                  size={currentTheme.dimensions.controlBarButtonSize}
                />
              }
              onClick={() => handleMenuToggle("input")}
              style={{ marginBottom: 16 }}
              tooltip="Add Input Node"
              tooltipPlacement="right"
            />
            <ControlBarButton
              selected={selected === "transform"}
              icon={
                <FaExchangeAlt
                  size={currentTheme.dimensions.controlBarButtonSize}
                />
              }
              onClick={() => handleMenuToggle("transform")}
              style={{ marginBottom: 16, color: currentTheme.colors.text }}
              tooltip="Add Transform Node"
              tooltipPlacement="right"
            />
            <div
              style={{
                height: 1,
                width: "65%",
                margin: "10px 0",
                borderRadius: 1,
                background: currentTheme.colors.text,
              }}
            />
            <ControlBarButton
              selected={selected === "output"}
              icon={
                <FaSignOutAlt
                  size={currentTheme.dimensions.controlBarButtonSize}
                />
              }
              onClick={() => addOutput()}
              tooltip="Add Output Node"
              tooltipPlacement="right"
            />
            <ControlBarButton
              id="add-composer-node-btn"
              dataTestid="add-composer-node-btn"
              selected={selected === "composer"}
              icon={
                <FaLayerGroup
                  size={currentTheme.dimensions.controlBarButtonSize}
                />
              }
              onClick={() => addComposer()}
              tooltip="Add Composer Node"
              tooltipPlacement="right"
            />
          </div>
          {/* --- Control Bar END --- */}

          {showTransformMenu && <TransformSidePanel show={showTransformMenu} />}

          {showInputMenu && <InputsSidePanel show={showInputMenu} />}

          <div
            ref={canvasContainerRef}
            style={{ flex: 1, height: "100%" }}
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()} // Required to drop something on
          >
            <ReactFlow
              onEdgeClick={handleEdgeClick}
              deleteKeyCode={null}
              onEdgeContextMenu={handleEdgeContextMenu}
              onPaneClick={closeEdgeMenu}
              onNodeClick={closeEdgeMenu}
              nodes={projectState.rfNodes}
              edges={projectState.rfEdges}
              defaultEdgeOptions={{
                style: {
                  stroke: currentTheme.colors.edgeColor,
                  strokeWidth: currentTheme.edgeStrokeWidth.default,
                },
              }}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onInit={setReactFlowInstance}
              onConnect={onConnect}
              snapToGrid={true}
              nodeTypes={nodeTypes}
              style={{
                width: `calc(100% - ${DEFAULT_TRANSFORM_PANEL_WIDTH}px)`,
                height: "100%",
              }}
              onMouseDownCapture={captureMouseEventForUserMenu}
            >
              {contextMenu && (
                <EdgeContextMenu
                  x={contextMenu.x}
                  y={contextMenu.y}
                  edge={contextMenu.edge}
                  onClose={closeEdgeMenu}
                  projectDispatch={projectDispatch}
                />
              )}
              <Controls
                position="bottom-right"
                orientation="horizontal"
                showZoom={false}
                showFitView={false}
                showInteractive={false}
              >
                <FlowZoomControls />
                <TooltipControlButton
                  tooltip={isFocusing ? "Normal View" : "Focus Mode"}
                  testId="focus-mode-button"
                  onClick={() => setIsFocusing((prev) => !prev)}
                  icon={
                    isFocusing ? (
                      <GoScreenNormal style={ICON_STYLE} />
                    ) : (
                      <GoScreenFull style={ICON_STYLE} />
                    )
                  }
                />
              </Controls>
              {canvasBackground !== NO_CANVAS_BACKGROUND && (
                <Background
                  gap={10}
                  variant={canvasBackground}
                  color={currentTheme.colors.canvasPatternColor}
                />
              )}
            </ReactFlow>
          </div>
        </div>
      </div>
    </>
  );
}
